import store from 'store';
import MasterData from '@spike/masterdata-model';
import {
    BoardingService,
    EvaluationQuestion,
    TransportationService,
    TrainingService,
    WalkingService,
    BoardingOffer
} from 'model/Service';
import Marketplace from '@spike/marketplace-model';
import {
    ServiceDto,
    FixedPriceOptionDto,
    VariablePriceDto,
    VariableDurationDto,
    EvaluationQuestionDto,
    ServicesPaginatedDto,
    DaycareServiceDto,
    ServiceItemDto,
    BoardingServiceDto,
    TransportationServiceDto,
    TrainingServiceDto,
    WalkingServiceDto,
    BoardingOfferDto
} from './ServicesDtos';
import {
    Service,
    FixedPriceOption,
    VariablePrice,
    ServiceDuration,
    Duration,
    VariableDuration,
    createBusinessAreaItem,
    EvaluationExpiry,
    Meta,
    MetadataDuration,
    DaycareMetadata,
    BoardingMetadata,
    TransportationMetadata,
    TrainingMetadata,
    WalkingMetadata,
    getDefaultDaycareMetadata,
    getDefaultBoardingMetadata,
    getDefaultTransportationMetadata,
    getDefaultTrainingMetadata,
    getDefaultWalkingMetadata,
    SESSION_BOOKING_TYPES,
    resolveBookingType,
    resolveMaxPets,
    LATE_PICKUP_FEE_TYPES,
    LATE_CHECKOUT_FEE_TYPES,
    SERVICES_BUSINESS_AREAS,
    DaycareService,
    ServiceItem,
    SERVICE_ITEM_TYPES
} from 'model/Service';
import { Option, PetTypeIds, OrderedOption } from '@spike/model';
import moment from 'moment';
import { convertToRoom } from '@spike/rooms-action';

export const convertToService = (serviceDto: ServiceDto): Service => {
    const masterData: MasterData = store.getState().masterData.data;
    return {
        id: serviceDto.id,
        uuid: serviceDto.uuid,
        siblingUuid: serviceDto.sibling_uuid,
        marketplaceId: serviceDto.marketplace_id,
        name: serviceDto.name,
        description: serviceDto.description || '',
        duration: convertDuration(serviceDto),
        pricing: {
            taxable: serviceDto.includes_tax,
            taxes: serviceDto.taxes
                .sort(function (a, b) {
                    return moment(a.created_at) > moment(b.created_at)
                        ? 1
                        : moment(b.created_at) > moment(a.created_at)
                        ? -1
                        : 0;
                })
                .map(tax => ({
                    id: tax.id,
                    uuid: tax.uuid,
                    name: tax.name,
                    percentage: tax.value
                })),
            fixedPrice: convertFixedPrice(serviceDto),
            variablePrice: convertVariablePrice(serviceDto)
        },
        businessArea: masterData.businessAreas.find(ba => ba.id === serviceDto.business_area)!,
        petTypes: masterData.petTypes.filter(pt => serviceDto.pet_list.includes(pt.id))!,
        staff: serviceDto.staff.map(s => ({
            id: s.id,
            firstName: s.first_name,
            lastName: s.last_name,
            avatar: s.image
        })),
        status: masterData.status.find(status => status.id === serviceDto.status_id)!,
        tags: serviceDto.tag_list,
        destroyable: serviceDto.destroyable,
        editable: serviceDto.editable,
        hasReport: serviceDto.has_report,
        houseCall: serviceDto.house_call,
        olbEnabled:
            serviceDto.olb_enabled === undefined || serviceDto.olb_enabled === null ? true : serviceDto.olb_enabled,
        forms: (serviceDto.forms || []).map(form => ({
            id: Number(form.id),
            title: form.title,
            sendCondition: form.send_condition,
            active: form.active
        })),
        color: serviceDto.color,
        // Service-specific metadata (parsed and typed)
        ...(serviceDto.business_area === SERVICES_BUSINESS_AREAS.DAYCARE &&
            serviceDto.variable_duration && {
                daycareData: convertDaycareMetadata(serviceDto.variable_duration)
            }),
        ...(serviceDto.business_area === SERVICES_BUSINESS_AREAS.BOARDING &&
            serviceDto.variable_duration && {
                boardingData: convertBoardingMetadata(serviceDto.variable_duration)
            }),
        ...(serviceDto.business_area === SERVICES_BUSINESS_AREAS.TRANSPORTATION &&
            serviceDto.variable_duration && {
                transportationData: convertTransportationMetadata(serviceDto.variable_duration)
            }),
        ...(serviceDto.business_area === SERVICES_BUSINESS_AREAS.TRAINING &&
            serviceDto.variable_duration && {
                trainingData: convertTrainingMetadata(serviceDto.variable_duration)
            }),
        ...(serviceDto.business_area === SERVICES_BUSINESS_AREAS.WALKING &&
            serviceDto.variable_duration && {
                walkingData: convertWalkingMetadata(serviceDto.variable_duration)
            }),
        // Daycare-specific fields
        dropOffTime:
            serviceDto.drop_off_start && serviceDto.drop_off_end
                ? {
                      start: serviceDto.drop_off_start,
                      end: serviceDto.drop_off_end
                  }
                : undefined,
        pickUpTime:
            serviceDto.pick_up_start && serviceDto.pick_up_end
                ? {
                      start: serviceDto.pick_up_start,
                      end: serviceDto.pick_up_end
                  }
                : undefined,
        // Boarding-specific fields
        checkInTime:
            serviceDto.check_in_start && serviceDto.check_in_end
                ? {
                      start: serviceDto.check_in_start,
                      end: serviceDto.check_in_end
                  }
                : undefined,
        checkOutTime:
            serviceDto.check_out_start && serviceDto.check_out_end
                ? {
                      start: serviceDto.check_out_start,
                      end: serviceDto.check_out_end
                  }
                : undefined,
        // Service items (used for Boarding)
        ...(serviceDto.service_items && {
            serviceItems: serviceDto.service_items.map(convertToServiceItem)
        }),
        marketplaces: convertMarketplaces(serviceDto)
    };
};

const convertMarketplaces = (serviceDto: ServiceDto): Service['marketplaces'] => {
    if (serviceDto.marketplaces && serviceDto.marketplaces.length > 0) {
        return serviceDto.marketplaces.map(marketplace => ({
            marketplaceId: marketplace.marketplace_id,
            staffIds: marketplace.staff_ids ?? [],
            taxes: marketplace.tax_ids ?? [],
            roomIds: marketplace.room_ids ?? [],
            roomUnitIds: marketplace.room_unit_ids ?? [],
            vehicleIds: marketplace.vehicle_ids ?? []
        }));
    }

    // Legacy services without a marketplaces payload still expose a single location.
    if (serviceDto.marketplace_id) {
        return [
            {
                marketplaceId: serviceDto.marketplace_id,
                staffIds: (serviceDto.staff ?? []).map(staff => staff.id),
                taxes: (serviceDto.taxes ?? []).map(tax => tax.id!),
                roomIds: [],
                roomUnitIds: [],
                vehicleIds: []
            }
        ];
    }

    return [];
};

const convertDaycareMetadata = (metadataJson: string): DaycareMetadata => {
    try {
        const raw = JSON.parse(metadataJson);
        return {
            durationOption: raw.duration_option || 'full_day',
            durationValue: raw.duration_value || 'Up to 8 hours',
            latePickupFee: {
                enabled: raw.late_pickup_fee?.enabled || false,
                type: raw.late_pickup_fee?.type || LATE_PICKUP_FEE_TYPES.PER_HOUR,
                gracePeriod: raw.late_pickup_fee?.grace_period || 15,
                fees: raw.late_pickup_fee?.fees || {}
            },
            roomsEnabled: raw.rooms_enabled || false,
            selectedRooms: raw.selected_rooms || []
        };
    } catch (error) {
        return getDefaultDaycareMetadata();
    }
};

const convertBoardingMetadata = (metadataJson: string): BoardingMetadata => {
    try {
        const raw = JSON.parse(metadataJson);
        return {
            dropoff24h: raw.dropoff_24h || false,
            roomsEnabled: raw.rooms_enabled || false,
            selectedRooms: raw.selected_rooms || [],
            minNights: raw.min_nights || 1,
            latePickupFee: {
                enabled: raw.late_pickup_fee?.enabled || false,
                type: raw.late_pickup_fee?.type || LATE_CHECKOUT_FEE_TYPES.FIXED_FEE,
                gracePeriod: raw.late_pickup_fee?.grace_period || 30,
                fees: raw.late_pickup_fee?.fees || {}
            }
        };
    } catch (error) {
        return getDefaultBoardingMetadata();
    }
};

const convertTransportationMetadata = (metadataJson: string): TransportationMetadata => {
    try {
        const raw = JSON.parse(metadataJson);
        return {
            vehiclesEnabled: raw.vehicles_enabled ?? true,
            selectedVehicles: raw.selected_vehicles || []
        };
    } catch (error) {
        return getDefaultTransportationMetadata();
    }
};

const convertTrainingMetadata = (metadataJson: string): TrainingMetadata => {
    try {
        const raw = JSON.parse(metadataJson);
        return {
            hours: raw.hours ?? 1,
            minutes: raw.minutes || 0,
            bookingType: raw.booking_type || 'private_session',
            maxPets: raw.max_pets || 1,
            inHomeService: raw.in_home_service || false,
            apptsCount: raw.appts_count || 0
        };
    } catch (error) {
        return getDefaultTrainingMetadata();
    }
};

const convertWalkingMetadata = (metadataJson: string): WalkingMetadata => {
    try {
        const raw = JSON.parse(metadataJson);
        return {
            hours: raw.hours ?? 1,
            minutes: raw.minutes ?? 0,
            bookingType: raw.booking_type || SESSION_BOOKING_TYPES.PRIVATE_SESSION,
            maxPets: raw.max_pets || 1,
            inHomeService: raw.in_home_service || false
        };
    } catch (error) {
        return getDefaultWalkingMetadata();
    }
};

export const convertToEvaluationService = (serviceDto: ServiceDto): Service => {
    const baseService = convertToService(serviceDto);

    return {
        ...baseService,
        businessArea: createBusinessAreaItem(serviceDto.business_area),
        evaluation: {
            id: serviceDto.evaluation?.id,
            category: serviceDto.evaluation?.category_list || [],
            evaluationExpiry: {
                enabled: serviceDto.evaluation?.evaluation_expiry_enabled,
                duration: serviceDto.evaluation?.evaluation_expiry
            } as EvaluationExpiry,
            evaluationQuestionsEnabled: serviceDto.evaluation?.custom_question_enabled || false,
            evaluationQuestions:
                serviceDto.evaluation?.custom_questions?.map(
                    (q: EvaluationQuestionDto) =>
                        ({
                            id: q?.id,
                            questionText: q.question_text,
                            enabled: q.enabled,
                            required: q.required,
                            displayOrder: q.display_order,
                            category: q.category,
                            questionableType: q.questionable_type,
                            questionableId: q.questionable_id
                        } as EvaluationQuestion)
                ) ?? []
        }
    };
};

export const convertToMeta = (servicesDto: ServicesPaginatedDto): Meta => {
    return {
        currentPage: servicesDto.current_page,
        perPage: servicesDto.per_page,
        totalServices: servicesDto.total_services,
        totalPages: servicesDto.total_pages
    };
};

//Business areas that use metadata
const isMetadataBusinessArea = (businessArea: string): boolean => {
    return (
        businessArea === SERVICES_BUSINESS_AREAS.DAYCARE ||
        businessArea === SERVICES_BUSINESS_AREAS.BOARDING ||
        businessArea === SERVICES_BUSINESS_AREAS.TRANSPORTATION ||
        businessArea === SERVICES_BUSINESS_AREAS.TRAINING ||
        businessArea === SERVICES_BUSINESS_AREAS.WALKING
    );
};

const convertDuration = (serviceDto: ServiceDto): ServiceDuration | MetadataDuration => {
    // Check if this is a service with metadata (Daycare, Boarding, etc.)
    if (isMetadataBusinessArea(serviceDto.business_area) && serviceDto.variable_duration) {
        return {
            metadata: serviceDto.variable_duration
        } as MetadataDuration;
    }

    // Regular ServiceDuration for non-metadata services
    return {
        simple: convertSimpleDuration(serviceDto),
        variable: convertVariableDuration(serviceDto)
    };
};

const convertSimpleDuration = (serviceDto: ServiceDto): Duration | undefined => {
    return serviceDto.duration_hours !== null && serviceDto.duration_minutes !== null
        ? {
              hours: serviceDto.duration_hours,
              minutes: serviceDto.duration_minutes
          }
        : undefined;
};

const convertVariableDuration = (serviceDto: ServiceDto): Array<VariableDuration> => {
    const marketplace = store.getState().marketplace.marketplace;
    const petSizes = marketplace.petTypes.dogs?.sizes || [];

    return serviceDto.variable_duration
        ? JSON.parse(serviceDto.variable_duration).map((vd: VariableDurationDto) => {
              const petSize = petSizes.find(ps => ps.element.id === vd.size_meta_variable);
              return {
                  petSize: {
                      id: petSize?.element.id,
                      name: petSize?.element.name
                  },
                  duration: { hours: vd.hours, minutes: vd.minutes }
              };
          })
        : [];
};

const convertFixedPrice = (serviceDto: ServiceDto): Array<FixedPriceOption> => {
    const masterData: MasterData = store.getState().masterData.data;
    if (serviceDto.fixed_price === null) {
        return [];
    }

    const responseFixedPrice: Array<FixedPriceOptionDto> = JSON.parse(serviceDto.fixed_price);

    return responseFixedPrice.map(fp => {
        const option: FixedPriceOption = {
            petType: masterData.petTypes.find(pt => pt.id === fp.pet_type_id)!
        };

        // Grooming/Dental/Daycare: uses 'price'
        if (fp.price !== undefined) {
            option.price = fp.price;
        }

        // Transportation: uses 'transportationPrice' (nested)
        if (fp.pickup_price !== undefined && fp.dropoff_price !== undefined) {
            option.transportationPrice = {
                pickUpPrice: fp.pickup_price,
                dropOffPrice: fp.dropoff_price
            };
        }

        return option;
    });
};

const convertVariablePrice = (serviceDto: ServiceDto): VariablePrice | undefined => {
    const masterData: MasterData = store.getState().masterData.data;
    const marketplace: Marketplace = store.getState().marketplace.marketplace;

    if (serviceDto.variable_price === null || serviceDto.variable_price === '') {
        return undefined;
    } else {
        const responseVariablePrice: VariablePriceDto = JSON.parse(serviceDto.variable_price);
        return {
            variables: {
                petTypeVariable: responseVariablePrice.variables.pet_type_variable,
                petSizeVariable: responseVariablePrice.variables.pet_size_variable,
                hairTypeVariable: responseVariablePrice.variables.hair_type_variable,
                hairLengthVariable: responseVariablePrice.variables.hair_length_variable,
                exoticTypeVariable: responseVariablePrice.variables.exotic_type_variable
            },
            prices: responseVariablePrice.prices.map(vp => {
                const petType = masterData.petTypes.find(pt => pt.id === vp.pet_type_id);
                let petSize: Option<string> | undefined = marketplace.petTypes.dogs?.sizes
                    .map(s => s.element)
                    .find(s => s.id === vp.size_meta_variable);
                let hairType =
                    petType?.id === 'dogs'
                        ? marketplace.petTypes.dogs?.hairTypes
                              .map(ht => ht.element)
                              .find(ht => ht.id === vp.hair_type_meta_variable)
                        : marketplace.petTypes.cats?.hairTypes
                              .map(ht => ht.element)
                              .find(ht => ht.id === vp.hair_type_meta_variable);
                let hairLength =
                    petType?.id === 'dogs'
                        ? marketplace.petTypes.dogs?.hairLengths
                              .map(ht => ht.element)
                              .find(ht => ht.id === vp.hair_type_meta_variable)
                        : marketplace.petTypes.cats?.hairLengths
                              .map(ht => ht.element)
                              .find(ht => ht.id === vp.hair_type_meta_variable);
                let exoticType = marketplace.petTypes.exotics?.types.find(e => e.id === vp.exotic_type_meta_variable);

                if (petSize === undefined) {
                    const masterDataPetSizeOption = masterData.petSizes
                        .map(s => s.element)
                        .find(s => s.id === vp.size_meta_variable);
                    if (masterDataPetSizeOption !== undefined) {
                        petSize = {
                            ...masterDataPetSizeOption
                        };
                    }
                }

                if (hairType === undefined) {
                    let masterDataHaitTypes: Array<OrderedOption<string>> = [];

                    if (petType?.id === PetTypeIds.DOGS) {
                        masterDataHaitTypes = masterData.dogHairTypes;
                    } else if (petType?.id === PetTypeIds.CATS) {
                        masterDataHaitTypes = masterData.catHairTypes;
                    }
                    hairType = masterDataHaitTypes
                        .map(ht => ht.element)
                        .find(ht => ht.id === vp.hair_type_meta_variable);
                }

                if (hairLength === undefined) {
                    let masterDataHairLengths: Array<OrderedOption<string>> = [];

                    if (petType?.id === PetTypeIds.DOGS) {
                        masterDataHairLengths = masterData.dogHairLengths;
                    } else if (petType?.id === PetTypeIds.CATS) {
                        masterDataHairLengths = masterData.catHairLengths;
                    }
                    hairLength = masterDataHairLengths
                        .map(hl => hl.element)
                        .find(hl => hl.id === vp.hair_length_meta_variable);
                }

                if (exoticType === undefined) {
                    exoticType = marketplace.petTypes.exotics?.others
                        .map(other => ({ id: other, name: other }))
                        .find(other => other.id === vp.exotic_type_meta_variable);
                }

                if (exoticType === undefined) {
                    exoticType = masterData.petExoticTypes.find(et => et.id === vp.exotic_type_meta_variable);
                }

                return {
                    id: vp.id,
                    uuid: vp.uuid,
                    petType,
                    petSize,
                    hairType,
                    hairLength,
                    exoticType,
                    price: vp.price
                };
            })
        };
    }
};

export const convertToDaycareServices = (daycareServicesDto: Array<DaycareServiceDto>): Array<DaycareService> => {
    return daycareServicesDto.map(ds => ({
        serviceId: ds.service_id,
        serviceName: ds.service_name,
        price: ds.price,
        duration: ds.duration,
        serviceColor: ds.color,
        dropOffTime: {
            start: ds.drop_off_start,
            end: ds.drop_off_end
        },
        pickUpTime: {
            start: ds.pick_up_start,
            end: ds.pick_up_end
        },
        latePickupFee: ds.late_pickup_fee
            ? {
                  enabled: ds.late_pickup_fee.enabled,
                  type: ds.late_pickup_fee.type,
                  gracePeriod: ds.late_pickup_fee.grace_period,
                  fees: ds.late_pickup_fee.fees
              }
            : undefined,
        variableDuration: ds.variable_duration
    }));
};

const resolveBoardingCatalogPrice = (row: Record<string, unknown>): number => {
    const price = Number(row.price ?? 0);
    const explicit =
        row.original_price ??
        row.originalPrice ??
        row.standard_price ??
        row.standardPrice ??
        row.default_price ??
        row.defaultPrice ??
        row.catalog_price ??
        row.catalogPrice ??
        row.base_price ??
        row.basePrice;
    if (explicit != null && explicit !== '' && Number.isFinite(Number(explicit))) {
        return Number(explicit);
    }
    return price;
};

const offersFromBoardingRow = (row: Record<string, unknown>): Array<BoardingOfferDto> | undefined => {
    const r = row;
    const list = (r.offers ?? r.boarding_offers ?? r.deals ?? r.boardingOffers) as unknown;
    if (!Array.isArray(list) || list.length === 0) {
        return undefined;
    }
    return list as Array<BoardingOfferDto>;
};

export const convertToBoardingServices = (boardingServicesDto: Array<BoardingServiceDto>): Array<BoardingService> => {
    return boardingServicesDto.map(ds => {
        const r = ds as unknown as Record<string, unknown>;
        const late = (r.late_pickup_fee ?? r.latePickupFee) as BoardingServiceDto['late_pickup_fee'];
        const price = Number(r.standard_price ?? 0);
        const catalogPrice = resolveBoardingCatalogPrice(r);
        const appliedOfferDto = (r.applied_offer ?? r.appliedOffer) as unknown;
        const appliedOffer =
            appliedOfferDto && typeof appliedOfferDto === 'object'
                ? convertToBoardingOffer(appliedOfferDto as BoardingOfferDto)
                : undefined;
        return {
            serviceId: Number(r.service_id ?? r.serviceId),
            serviceName: String(r.service_name ?? r.serviceName ?? ''),
            price,
            catalogPrice,
            duration: Number(r.duration ?? 0),
            serviceColor: (r.color ?? r.serviceColor) as string | undefined,
            minimumNights: Number(r.minimum_nights ?? r.minimumNights ?? 0) || 0,
            checkInTime: {
                start: Number(r.check_in_start ?? r.checkInStart ?? 0),
                end: Number(r.check_in_end ?? r.checkInEnd ?? 0)
            },
            checkOutTime: {
                start: Number(r.check_out_start ?? r.checkOutStart ?? 0),
                end: Number(r.check_out_end ?? r.checkOutEnd ?? 0)
            },
            latePickupFee: late
                ? {
                      enabled: late.enabled,
                      type: late.type,
                      gracePeriod: late.grace_period,
                      fees: late.fees
                  }
                : undefined,
            serviceItems: ((r.service_items ?? r.serviceItems ?? []) as Array<ServiceItemDto>).map(
                convertToServiceItem
            ),
            rooms: ((r.rooms ?? []) as BoardingServiceDto['rooms']).map(room =>
                convertToRoom(room, store.getState().masterData.data)
            ),
            offers: offersFromBoardingRow(r)?.map(offer => convertToBoardingOffer(offer)),
            appliedOffer
        };
    });
};

export const convertToBoardingOffer = (offerDto: BoardingOfferDto): BoardingOffer => {
    return {
        offerType: offerDto.offer_type,
        id: offerDto.id,
        uuid: offerDto.uuid,
        total: offerDto.total,
        pricePerNight: offerDto.price_per_night,
        name: offerDto.name,
        startDate: offerDto.start_date ?? null,
        endDate: offerDto.end_date ?? null
    };
};

export const convertToTransportationServices = (dto: Array<TransportationServiceDto>): Array<TransportationService> => {
    return dto.map(s => ({
        serviceId: s.service_id,
        serviceName: s.service_name,
        pickupPrice: s.pickup_price,
        dropoffPrice: s.dropoff_price,
        duration: s.duration,
        color: s.color || ''
    }));
};

export const convertToTrainingServices = (dto: Array<TrainingServiceDto>): Array<TrainingService> => {
    return dto.map(s => ({
        serviceId: s.service_id,
        serviceName: s.service_name,
        price: s.price,
        duration: s.duration,
        color: s.color || '',
        inHomeService: s.in_home_service ?? false,
        bookingType: resolveBookingType(s.booking_type),
        maxPets: resolveMaxPets(s.booking_type, s.max_pets)
    }));
};

export const convertToWalkingServices = (dto: Array<WalkingServiceDto>): Array<WalkingService> => {
    return dto.map(s => ({
        serviceId: s.service_id,
        serviceName: s.service_name,
        price: s.price,
        duration: s.duration,
        color: s.color || '',
        inHomeService: s.in_home_service ?? false,
        bookingType: resolveBookingType(s.booking_type),
        maxPets: resolveMaxPets(s.booking_type, s.max_pets)
    }));
};

// API -> UI label
const mapServiceItemTypeFromApi = (t: string): string => {
    switch (t) {
        case 'Service':
            return 'Primary Service'; // Use generic type, frontend will determine specific type
        case 'BusinessProduct':
            return SERVICE_ITEM_TYPES.PRODUCT;
        case 'AddonService':
            return SERVICE_ITEM_TYPES.ADD_ON;
        default:
            return t; // fallback
    }
};

// UI label -> API
export const mapServiceItemTypeToApi = (label: string): 'Service' | 'BusinessProduct' | 'AddonService' => {
    switch (label) {
        case SERVICE_ITEM_TYPES.GROOMING_SERVICE:
        case SERVICE_ITEM_TYPES.DAYCARE_SERVICE:
            return 'Service';
        case SERVICE_ITEM_TYPES.PRODUCT:
            return 'BusinessProduct';
        case SERVICE_ITEM_TYPES.ADD_ON:
            return 'AddonService';
        default:
            return 'Service';
    }
};

const convertToServiceItem = (dto: ServiceItemDto): ServiceItem => ({
    id: dto.id,
    uuid: dto.uuid,
    marketplaceID: dto.marketplace_id,
    serviceID: dto.service_id,
    businessArea: dto.business_area,
    itemID: dto.item_id,
    itemType: mapServiceItemTypeFromApi(dto.item_type),
    itemName: dto.name, // Changed from dto.item_name to dto.name to match backend response
    amount: dto.amount,
    quantity: dto.quantity,
    perStay: dto.per_stay,
    duration: dto.duration
});
